/**
 * About `/about` — the founders' page. Static content: two equal-weight
 * founder cards (facts pinned to the same baseline), one condensed
 * four-eras story panel with Deej walking the timeline, and nothing longer
 * than it needs to be (owner copy review, 2026-07-15). Facts sourced from
 * the founders' LinkedIn profiles, SEC filings, and press coverage.
 */
import { Mascot } from '../components/Mascot.js';

interface FounderFact {
  label: string;
  detail: string;
}

interface Founder {
  name: string;
  handle: string;
  role: string;
  linkedin: string;
  story: string[];
  facts: FounderFact[];
}

const FOUNDERS: Founder[] = [
  {
    name: 'Jon Achai',
    handle: 'jon',
    role: 'Co-founder · Chief Everything Officer',
    linkedin: 'https://www.linkedin.com/in/jon-achai/',
    story: [
      'A computer-science engineer since childhood, Jon has spent 25+ years shipping ' +
        'consumer internet at scale: eight years at NYC\'s Oddcast — the shop behind ' +
        'SitePal and the ElfYourself craze (2 billion elves and counting) — then ' +
        'founding engineer at YouNow, building the backend and video infrastructure ' +
        'that carried the first US mobile live-streaming app past 100 million users.',
      'As Chief Engineer through the Props era, he wrote the OpenZeppelin-audited ' +
        'Ethereum contracts behind the first consumer token ever qualified by the ' +
        'SEC — a million-holder rewards program — then took the playbook to web3 ' +
        'collectibles at Revel.xyz. B.A. in Computer Science, Hunter College.',
    ],
    facts: [
      { label: 'First SEC-qualified consumer token', detail: 'led engineering on Props (Reg A+, 2019)' },
      { label: '100M+ users', detail: 'built YouNow\'s backend + video infra from scratch' },
      { label: '2,000,000,000 elves', detail: 'eight Oddcast years behind ElfYourself + SitePal' },
      { label: 'Smart contracts', detail: 'ERC-20 + rewards upgrade, OpenZeppelin-audited' },
    ],
  },
  {
    name: 'Donald DeWulf',
    handle: 'donald',
    role: 'Co-founder · Master of Backends, DevOps & Infra',
    linkedin: 'https://www.linkedin.com/in/donalddewulf/',
    story: [
      'The engineer you want between your product and a hundred million sessions a ' +
        'month. Trained in Ghent, Belgium, Donald spent seven years keeping YouNow — ' +
        'one of the internet\'s busiest live platforms — on the air: high-concurrency ' +
        'APIs for iOS, Android and web, ~100 million user sessions a month, 150,000 ' +
        'live streams a day.',
      'Along the way he carved the monoliths into Kubernetes microservices, built ' +
        'the clusters himself, and load-tested everything that moved — then carried ' +
        'the same playbook to Revel.xyz\'s serverless web3 backends. If it scales, ' +
        'shards, or autoscales, Donald has probably tuned it.',
    ],
    facts: [
      { label: '100M sessions / month', detail: 'YouNow\'s high-concurrency API layer' },
      { label: '150,000 live streams / day', detail: 'kept them all on the air' },
      { label: 'Monolith → microservices', detail: 'built the Kubernetes clusters himself' },
      { label: 'AWS deep cuts', detail: 'DynamoDB, Redshift, Aurora, ElastiCache & friends' },
    ],
  },
];

export function About() {
  return (
    <div className="container">
      <div className="gallery__hero">
        <h1 className="gallery__title">About us</h1>
        <p className="gallery__subtitle">
          Two engineers, one pigeon, and a decade of building the internet's playgrounds.
        </p>
      </div>

      <Mascot context="about" size={340} />

      <div className="panel" style={{ marginTop: 18 }}>
        <h2 className="panel__title">What is djgg?</h2>
        <p className="about__lead">
          djgg is a place where anyone can create a real, playable game — describe it, let
          the AI build it, iterate until it's yours, then share it, fork it, and battle
          your friends online. It's built by two people who have spent their careers
          making consumer platforms that millions of people actually use.
        </p>
      </div>

      <div className="about__grid">
        {FOUNDERS.map((f) => (
          <section key={f.handle} className="panel founder-card" data-testid={`founder-${f.handle}`}>
            <h2 className="panel__title" style={{ marginBottom: 2 }}>
              {f.name}
            </h2>
            <div className="founder-card__role">{f.role}</div>
            {f.story.map((p, i) => (
              <p key={i} className="founder-card__story">
                {p}
              </p>
            ))}
            <ul className="founder-card__facts">
              {f.facts.map((fact) => (
                <li key={fact.label}>
                  <strong>{fact.label}</strong> — {fact.detail}
                </li>
              ))}
            </ul>
            <a
              className="btn btn--sm"
              href={f.linkedin}
              target="_blank"
              rel="noreferrer noopener"
            >
              LinkedIn ↗
            </a>
          </section>
        ))}
      </div>

      <div className="panel">
        <h2 className="panel__title">One team, four eras</h2>
        <Mascot context="about-eras" size={720} />
        <p className="about__story">
          Jon and Donald met at YouNow — the first US mobile live-streaming app, with
          100M+ registered users, 150,000 broadcasts a day, and one of the first
          virtual-currency economies in American consumer apps. When YouNow spun that
          economy into Props, they shipped the first consumer token ever qualified by
          the SEC and ran its rails for over a million holders.
        </p>
        <p className="about__story">
          Revel.xyz came next — collectible games, same duo — and with it the idea they
          kept sketching between deploys: anyone should be able to imagine a game and be
          playing it minutes later. That idea is djgg. AI builds, you direct, everyone
          plays.
        </p>
        <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
          Sources: LinkedIn, SEC filings, and press in TechCrunch, CoinDesk and
          Bloomberg. The pigeon insisted on being mentioned too.
        </p>
      </div>
    </div>
  );
}
